import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { 
  FaCreditCard, 
  FaPlus, 
  FaEdit, 
  FaTrash,
  FaSearch
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import metodosPagoApi from '../../services/api/metodosPagoApi';
import 'react-toastify/dist/ReactToastify.css';

const MantenimientoMetodosPago = () => {
  const [metodosPago, setMetodosPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMetodoPago, setEditingMetodoPago] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    activo: true
  });

  useEffect(() => {
    loadMetodosPago();
  }, []);

  const loadMetodosPago = async () => {
    setLoading(true);
    try {
      const response = await metodosPagoApi.getMetodosPago(null); // Obtener todos, activos e inactivos
      if (response.data && response.data.data) {
        setMetodosPago(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar métodos de pago:', error);
      toast.error('Error al cargar métodos de pago');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setEditingMetodoPago(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      activo: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingMetodoPago) {
        await metodosPagoApi.updateMetodoPago(editingMetodoPago.id, formData);
        toast.success('Método de pago actualizado correctamente');
      } else {
        await metodosPagoApi.createMetodoPago(formData);
        toast.success('Método de pago creado correctamente');
      }

      setShowModal(false);
      resetForm();
      loadMetodosPago();
    } catch (error) {
      console.error('Error al guardar método de pago:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar método de pago');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (metodoPago) => {
    setEditingMetodoPago(metodoPago);
    setFormData({
      codigo: metodoPago.codigo || '',
      nombre: metodoPago.nombre || '',
      descripcion: metodoPago.descripcion || '',
      activo: metodoPago.activo !== undefined ? metodoPago.activo : true
    });
    setShowModal(true);
  };

  const handleDelete = async (metodoPagoId) => {
    if (!window.confirm('¿Está seguro de eliminar este método de pago?')) {
      return;
    }

    try {
      await metodosPagoApi.deleteMetodoPago(metodoPagoId);
      toast.success('Método de pago eliminado correctamente');
      loadMetodosPago();
    } catch (error) {
      console.error('Error al eliminar método de pago:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar método de pago');
    }
  };

  const filteredMetodosPago = metodosPago.filter(metodoPago => {
    const search = searchTerm.toLowerCase();
    return (
      metodoPago.codigo?.toLowerCase().includes(search) ||
      metodoPago.nombre?.toLowerCase().includes(search) ||
      metodoPago.descripcion?.toLowerCase().includes(search)
    );
  });

  return (
    <Container fluid className="p-4">
      <ToastContainer />
      <style>
        {`
          .dh-card-main {
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
            border: none;
          }
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.5rem;
          }
          .dh-header-title {
            font-weight: 600;
            color: white;
          }
        `}
      </style>

      <Row>
        <Col>
          <Card className="dh-card-main mb-4">
            <Card.Header className="dh-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FaCreditCard className="me-3" size={28} />
                  <h3 className="dh-header-title mb-0">Mantenimiento de Métodos de Pago</h3>
                </div>
                <Button
                  variant="light"
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  <FaPlus className="me-2" />
                  Nuevo Método de Pago
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Búsqueda */}
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      <FaSearch className="me-2" />
                      Buscar
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Buscar por código, nombre o descripción..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Tabla de métodos de pago */}
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Cargando métodos de pago...</p>
                </div>
              ) : filteredMetodosPago.length === 0 ? (
                <Alert variant="info">No se encontraron métodos de pago</Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Código</th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMetodosPago.map(metodoPago => (
                      <tr key={metodoPago.id}>
                        <td>{metodoPago.id}</td>
                        <td>{metodoPago.codigo}</td>
                        <td>{metodoPago.nombre}</td>
                        <td>{metodoPago.descripcion || '-'}</td>
                        <td>
                          {metodoPago.activo ? (
                            <Badge bg="success">Activo</Badge>
                          ) : (
                            <Badge bg="secondary">Inactivo</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEdit(metodoPago)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(metodoPago.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear/editar método de pago */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        resetForm();
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingMetodoPago ? 'Editar Método de Pago' : 'Nuevo Método de Pago'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Código *</Form.Label>
                  <Form.Control
                    type="text"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleInputChange}
                    required
                    maxLength={20}
                    placeholder="Ej: TRANSFERENCIA, YAPE"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    maxLength={255}
                    placeholder="Nombre del método de pago"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleInputChange}
                    placeholder="Descripción del método de pago (opcional)"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleInputChange}
                    label="Activo"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowModal(false);
              resetForm();
            }}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : (editingMetodoPago ? 'Actualizar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default MantenimientoMetodosPago;
